import ExcelJS from 'exceljs';
import ACTION_TYPES from '../redux/types';
import store from '../redux/store';
import { renderAlert } from '../CommonComponents/Alert/Alert';
import { getIndexesFromNodeArray, updateValue } from '../MiniProducts/xTables/helper/helpersSubModule';
import { uploadExcelToOneDrive } from '../redux/action/sheetAction';
import { containsRowId } from '../MiniProducts/xTables/helper/cellValueChangeHelpers';

const applyStyles = (cell, cellData, indent = 0, isMdv) => {
    if (cellData) {
        const { format, cellStyle } = cellData;

        const allowedFonts = ['Arial', 'Tahoma', 'Times', 'Verdana'];
        let fontFamily = 'Arial';

        if (cellStyle?.fontFamily && allowedFonts.includes(cellStyle.fontFamily)) {
            fontFamily = cellStyle.fontFamily;
            cell.font = {
                name: fontFamily
            }
        }

        if (cellStyle?.fontSize || isMdv) {
            const fontSize = cellStyle?.fontSize ? parseInt(cellStyle.fontSize.replace('px', ''), 10) : 10;
            cell.font = {
                ...cell.font,
                size: fontSize
            }
        }

        // Apply font styles
        if (format?.length > 0 || typeof cellStyle === 'object' && Object.keys(cellStyle).length > 0 && cellStyle.hasOwnProperty('color')) {
            cell.font = {
                ...cell.font,
                bold: format.includes('bold'),
                italic: format.includes('italic'),
                underline: format.includes('underline'),
                strike: format.includes('strikethrough'),
                color: { argb: cellStyle?.color ? cellStyle.color.replace('#', 'FF') : undefined }
            };
        }
        // Apply fill styles
        if (typeof cellStyle === 'object' && Object.keys(cellStyle).length > 0 && cellStyle.hasOwnProperty('backgroundColor')) {
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: cellStyle?.backgroundColor ? cellStyle.backgroundColor.replace('#', 'FF') : undefined }
            };
        }
    }
    cell.alignment = {
        indent: indent * 2,
        wrapText: true
    };
};

const getAllColumnHeaders = (gridRef) => {
    const allColumns = gridRef.current.api.getAllGridColumns();

    const allheaderNames = allColumns.map((col) => {
        const headerName = col.colDef.headerName;
        if (headerName !== '#') {
            return headerName;
        } else {
            return null;
        }
    }).filter(headerName => headerName !== null);

    return allheaderNames;
}


const getAllColumnfields = (gridRef) => {
    const allColumns = gridRef.current.api.getAllGridColumns();
    const colsWithParent = allColumns.filter((col) => { if (col.colDef.parent) { return true } })
    const allFields = allColumns.map((col) => {
        const field = col.colDef.field;

        if (field !== '#' && field !== 'primaryCol' && field !== undefined) {
            return `${field}_es`;
        } else {
            return null;
        }
    }).filter(field => field !== null);
    return allFields;
}

const getAllColumnTypes = (gridRef) => {
    const allColumns = gridRef.current.api.getAllGridColumns();

    const fieldTypeMap = allColumns.reduce((acc, col) => {
        const field = col.colDef.field;

        if (field !== '#' && field !== 'primaryCol' && field !== undefined) {
            const key = `${field}_es`;
            const value = col.colDef.type;
            acc[key] = value;
        }

        return acc;
    }, {});

    return fieldTypeMap;
}

const getAllColumnWidths = (gridRef) => {
    const allColumns = gridRef.current.api.getAllGridColumns();
    const widthArray = []
    const allWidths = allColumns.map((col) => {
        const field = col.colDef.field;
        const width = col.actualWidth;
        if (field === '#') {
            return null;
        } else if (field === 'primaryCol') {
            widthArray[0] = width
        } else {
            widthArray.push(width)
        }
    });
    return widthArray;
}
const findPredeccessorCol = (gridRef) => {
    const allColumns = gridRef.current.api.getAllGridColumns();
    const allFields = allColumns.map((col) => {
        if (col.colDef.type === "predecessor")
            return `${col.colDef.field}_es`
        else return null

    }).filter(field => field !== null);
    return allFields;
}

const calculateIndentationLevel = (id, l_id) => {
    if (id === l_id) {
        return 0;
    } else {
        let indentationLevel = l_id.split('.').length;
        if (indentationLevel > 1) {
            return indentationLevel - 1;
        } else {
            return 0;
        }
    }
}

export const convertAccordingToMdvFormat = (formatValue) => {
    formatValue = JSON.parse(formatValue);
    // Split the input into lines
    const lines = formatValue
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);

    const result = lines.map((line) => {
        // Split the line by the first colon
        const [namePart, ...rest] = line.split(":");
        const value = rest.join(":").trim();

        // Match the pattern
        const match = namePart.match(/^(\w+(\s\w+)*)\s\[(\w+)\]\s\[(\w+)\]$/);

        if (match) {
            const [_, propName, , propAbbrevation, propType] = match;
            let propValue = value;

            if (propType === "number") {
                // Explicitly handle non-numeric values
                propValue = value.trim() === "" ? null : Number(value);
                if (isNaN(propValue)) {
                    propValue = null; // Set to null if conversion results in NaN
                }
            }
            return {
                propName,
                propAbbrevation,
                propType,
                propValue,
            };
        } else {
            return null;
        }
    });

    const filteredResult = result.filter((item) => item !== null);
    return JSON.stringify(filteredResult);
};

export const isStringifiedArrayWithPropName = (value) => {
    try {
        if (!value || value === "undefined" || value === "null") return false;
        let parsedValue;

        if (Array.isArray(value)) {
            parsedValue = value;
        } else if (typeof value === 'string') {
            try {
                parsedValue = JSON.parse(value);
            } catch (e) {
                parsedValue = value; // fallback to original string if not JSON
            }
        } else {
            parsedValue = value; // fallback for other types (e.g., object, number)
        }
        return Array.isArray(parsedValue) && parsedValue.some(obj => typeof obj === 'object' && obj !== null && 'propName' in obj);
        ;

    } catch (e) {
        if (process.env.NODE_ENV === 'development') {
            console.warn("Failed to parse JSON in cellValue:", e);
        }
        return false;
    }
};

export const parseAndSortValuesForPersonCol = (value) => {
    const parsedValue = value ? JSON.parse(value) : []
    if (Array.isArray(parsedValue) && parsedValue.every(item => item.username && item.email)) {
        return parsedValue
            .map(obj => `Username: ${obj.username}, User Email: ${obj.email}, initials: ${obj.initials}`); // Format output
    }
}

export const parseAndSortValuesForPersonColHelperForExport = (value) => {
    const parsedValue = value ? JSON.parse(value) : []
    if (Array.isArray(parsedValue) && parsedValue.every(item => item.username)) {
        return parsedValue.map(obj => obj.email ? `${obj.username} <${obj.email}>` : obj.username);
    }
}

export const parseAndSortValues = (value) => {
    const parsedValue = JSON.parse(value);
    return parsedValue
        .filter(obj => obj.propValue !== "" && obj.propValue !== null && obj.propValue !== undefined) // Filter out objects with empty propValue
        .map(obj => {
            const propValue = Array.isArray(obj.propValue)
                ? `(${obj.propValue.join(", ")})`
                : obj.propValue || '';

            return `${obj.propName} [${obj.propAbbrevation}] [${obj.propType}]: ${propValue}`;
        })
        .join(", ");
};

export const getFlatChildrenColumnOfTimeSeries = (columns, flatChildren = []) => {
    if (columns && columns.length) columns.forEach((col) => {
        if (col.field) flatChildren.push(col);
        else if (col.children) {
            return getFlatChildrenColumnOfTimeSeries(col.children, flatChildren)
        }
    })
    return flatChildren
}

const checkIfColGroupingExist = (gridRef) => {
    const colDefs = gridRef.current.api.getColumnDefs();
    let colGroupingExists = false;
    let groups = [];
    colDefs.map((col) => {
        if (col.marryChildren) {

            let isTimeSeries = isTheGroupTimeseries(col)

            colGroupingExists = true;

            let group = {
                name: col.headerName,
                children: col.children.map((child) => child.headerName)
            };
            if (isTimeSeries) {
                group.isTimeSeries = true
                group.children = getFlatChildrenColumnOfTimeSeries(col.children).map((child) => child.headerName)
            }
            groups.push(group);
        }
    })
    return { colGroupingExists, groups }
}

export function isTheGroupTimeseries(col) {
    let isTimeSeriesPresent = false;
    if (col.block && !col.parent) {
        try {
            let rangeInfo = JSON.parse(col.block);
            if (rangeInfo.startDate && rangeInfo.endDate) {
                isTimeSeriesPresent = true;
            }
        }
        catch (err) {
            console.warn('istimeseries:', err);
        }
    }
    return isTimeSeriesPresent;
}

function findMatchingPositions(headerNames, groups) {
    let positions = groups.map(group => {
        let startIndex = -1;
        let allFound = true;
        for (let i = 0; i < group.children.length; i++) {
            let found = false;
            for (let j = 0; j < headerNames.length; j++) {
                if (headerNames[j] === group.children[i]) {
                    if (startIndex === -1) {
                        startIndex = j;
                    }
                    found = true;
                    break;
                }
            }
            if (!found) {
                allFound = false;
                break;
            }
        }

        return allFound ? { startIndex, length: group.children.length, name: group.name } : null;
    });
    return positions.filter(position => position !== null);
}


export const downloadSheet = async (gridRef, fileName, sheetType, setIsDownloading, azureIntegrationInfo) => {
    try {
        setIsDownloading(true); // Show loaderconst workbook = new ExcelJS.Workbook();
        const workbook = new ExcelJS.Workbook();
        const worksheet = await createExcelJsWorkbook(gridRef, fileName, workbook, sheetType, '', false, azureIntegrationInfo);
        await downloadEntity(workbook, fileName)
    } catch (error) {
        console.error('Error downloading sheet:', error);
    } finally {
        setIsDownloading(false); // Hide loader, even if there's an error
    }
};


const getLastRowContent = (gridRef, colGroupingExists, sheetType, azureIntegrationInfo) => {
    const columnDefs = gridRef.current.api.getAllGridColumns()
        .map((col) => col.colDef)
        .filter((col) => col.field !== "#" && col.field !== "primaryCol");

    const firstCellPrefix = colGroupingExists
        ? "$%^text&Group"
        : "$%^text";

    const cells = [
        `${firstCellPrefix}||${sheetType}||${azureIntegrationInfo ? azureIntegrationInfo : false}||`
    ];

    const build = (...parts) => parts.filter(v => v !== "" && v !== undefined && v !== null).join("||");

    columnDefs.forEach((columnDef) => {
        const field = columnDef.field;
        const type = columnDef.type ?? "text";
        const isAzureColumn = sheetType === "filterSheet" ? false : columnDef.azureColumn ? columnDef.azureColumn : false;

        let value = "";

        switch (type) {
            case "dropdown":
            case "dropdownsingle": {
                const list = columnDef.listData?.length ? columnDef.listData.join(", ") : "";
                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    list,
                    columnDef.sharedlistId
                ) + "||";
                break;
            }

            case "shareableRollupList": {
                const defaultStatus = columnDef.defaultStatus ?? JSON.stringify({
                    value: 'Backlog',
                    parent: 'Default Status',
                    precedence: '0:0',
                    rowId: 'None',
                });

                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    columnDef.listData,
                    columnDef.sharedlistId,
                    defaultStatus
                ) + "||";
                break;
            }

            case "person":
            case "Assignee": {
                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    columnDef.listData
                ) + "||";
                break;
            }

            case "multi dimensional value": {
                const data = Array.isArray(columnDef.listData)
                    ? JSON.stringify(columnDef.listData)
                    : columnDef.listData;

                value = build(field, type, isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined, data) + "||";
                break;
            }

            case "number": {
                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    columnDef.rollup
                ) + "||";
                break;
            }

            case "date": {
                const linked = columnDef?.linkedColumns ? JSON.stringify(columnDef.linkedColumns) : "";
                const rollup = columnDef.rollup !== 'None' ? columnDef.rollup : "";

                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    rollup,
                    linked
                ) + "||";
                break;
            }

            case "duration":
            case "predecessor": {
                const linked = JSON.stringify(columnDef.linkedColumns);
                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    linked
                ) + "||";
                break;
            }

            case "estimatedTime": {
                const linked = JSON.stringify(columnDef.linkedColumns);
                value = build(
                    field,
                    type,
                    isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined,
                    columnDef.rollup,
                    linked
                ) + "||";
                break;
            }

            default: {
                value = build(field, type, isAzureColumn ? isAzureColumn : azureIntegrationInfo ? false : undefined) + "||";
            }
        }

        cells.push(value);
    });

    return cells;
};


const downloadEntity = async (workbook, fileName) => {
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fileName}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
}

export const downloadWorbook = (gridRef, start = false, currentWorkbook) => async (dispatch) => {
    if (!gridRef) {
        if (start) {
            dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, isWorkbookDownloading: true, isWorkbookDownloadingExcelJS: true, currentDownladingWorkbook: currentWorkbook, showWorkbookloader: true })
        }
        return
    }
    const { exportWorkbook, sheetsExportedIntoWorkbook, currentDownladingWorkbook } = store.getState().workbookReducer
    let sheetList = start ? checkSheetToDownloadInAWorkbook(currentWorkbook.sheetList) : checkSheetToDownloadInAWorkbook(currentDownladingWorkbook.sheetList);
    let workbookToBeDownloaded = start ? currentWorkbook : currentDownladingWorkbook;

    if (start) {
        dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, isWorkbookDownloading: true, isWorkbookDownloadingExcelJS: true, currentDownladingWorkbook: currentWorkbook, showWorkbookloader: true })
    }

    const workbook = exportWorkbook || new ExcelJS.Workbook();

    const rowData = [];
    gridRef.current.api.forEachNode(node => rowData.push(node.data));
    const sheetId = rowData[0].sheet_id;
    let fileName = '';
    sheetList.map(sheet => {
        if (sheet.id === sheetId) {
            fileName = sheet.name
        }
    })

    //
    if (!sheetsExportedIntoWorkbook.includes(fileName) && fileName.length > 0) {
        await dispatch(createSheetForDownload(gridRef, workbook, fileName, sheetsExportedIntoWorkbook, sheetList, workbookToBeDownloaded))
    }
}

export const exportWorkbookToOnedrive = (gridRef) => async (dispatch) => {
    const { exportWorkbook, sheetsExportedIntoWorkbook, workbooksExported, currentDownladingWorkbook } = store.getState().workbookReducer
    let newWorkbook = exportWorkbook || new ExcelJS.Workbook();
    try {
        if (gridRef) {
            const rowData = [];
            gridRef.current.api.forEachNode(node => rowData.push(node.data));
            const sheetId = rowData[0].sheet_id;
            const sheet = currentDownladingWorkbook.sheetList?.find((sh) => sh.id === sheetId);

            if (!sheetsExportedIntoWorkbook?.includes(sheet.id)) {
                dispatch(createSheetForDownloadForOnedrive(gridRef, newWorkbook, sheet, sheetsExportedIntoWorkbook, currentDownladingWorkbook.sheetList, currentDownladingWorkbook, workbooksExported, false, (workbook) => workbookUploadedCallback(workbook)))
            }
        }

    } catch (err) {
        renderAlert('Something went wrong, stopping onedrive backup', 'error', 2000)
        console.log('Something went wrong during upload workbook to onedrive', err);
        dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, currentDownladingWorkbook: {}, isWorkbookDownloading: false, exportWorkbook: null })
        dispatch({ type: ACTION_TYPES.SET_USER_ACTIVITY_INFO, takeOneDriveBackup: true })

    }
}

const workbookUploadedCallback = (workbook) => async (dispatch) => {
    const { workbooksSyncedWithOneDrive } = store.getState().userActivityReducer
    let syncedWbs = workbooksSyncedWithOneDrive
    syncedWbs[workbook.id] = true
    dispatch({ type: ACTION_TYPES.SET_USER_ACTIVITY_INFO, workbooksSyncedWithOneDrive: syncedWbs })
    if (!backupAllWorkbookstoOneDrive) return;
    console.log(syncedWbs, "syncedWbs", Object.keys(syncedWbs).length, workbookListData.length, "workbook", workbook.name)
    if (Object.keys(syncedWbs).length === workbookListData.length) {
        renderAlert("All Workbooks Uploaded to OneDrive", "success", '172800000')
        dispatch({ type: ACTION_TYPES.SET_USER_ACTIVITY_INFO, takeOneDriveBackup: false, backupAllWorkbookstoOneDrive: false, WorkBookUploadCallInProgress: false })

    }
}

const createSheetForDownload = (gridRef, workbook, fileName, sheetsExportedIntoWorkbook, sheetListData, currentSelectedWorkbook) => async (dispatch) => {
    let newSheetTypeIntoWorkbook = sheetListData.filter(item => item.name === fileName)[0].sheetType
    const worksheet = await createExcelJsWorkbook(gridRef, fileName, workbook, newSheetTypeIntoWorkbook, '', false)
    const newSheetListIntoWorkbook = [...sheetsExportedIntoWorkbook]
    newSheetListIntoWorkbook.push(fileName)

    dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, exportWorkbook: workbook, sheetsExportedIntoWorkbook: newSheetListIntoWorkbook, isWorkbookDownloading: true, isWorkbookDownloadingExcelJS: true, showWorkbookloader: true })

    if (newSheetListIntoWorkbook.length === checkSheetToDownloadInAWorkbook(sheetListData).length) {
        const workbookName = currentSelectedWorkbook.name;
        downloadEntity(workbook, workbookName)
        dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, isWorkbookDownloading: false, exportWorkbook: null, sheetsExportedIntoWorkbook: [], isWorkbookDownloadingExcelJS: false, currentDownladingWorkbook: {}, showWorkbookloader: false })
        const isWbTemplate = currentSelectedWorkbook.sheetList.some(obj => obj.sheetType === "checkinagent");
        let workbookUploadedMessage = '';
        if (isWbTemplate && (currentSelectedWorkbook.sheetList.filter((sheet) => sheet.sheetType !== 'xPad').length !== checkSheetToDownloadInAWorkbook(currentSelectedWorkbook.sheetList).length)) {
            workbookUploadedMessage += " CheckinagentInfo sheet not downloaded as WMT sheet does not have any Assignees "
        }
        const message = `Workbook Downloaded${workbookUploadedMessage ? "\n" + workbookUploadedMessage : ""}`;
        renderAlert(message, "success", 3000);
    }

}

export const createSheetForDownloadForOnedrive = (gridRef, workbook, sheet, sheetsExportedIntoWorkbook, sheetList, currentExportingWorbook, workbooksExported, backupAllWorkbookstoOneDrive, callback) => async (dispatch) => {
    try {
        const worksheet = await createExcelJsWorkbook(gridRef, sheet.name, workbook, sheet.sheetType, sheet.id, true);
        const newSheetListIntoWorkbook = [...sheetsExportedIntoWorkbook];
        newSheetListIntoWorkbook.push(sheet.id);
        const newWorkbooksExported = [...workbooksExported];

        if (!workbooksExported.includes(currentExportingWorbook.id)) {
            newWorkbooksExported.push(currentExportingWorbook.id)
        }

        if (newSheetListIntoWorkbook.length === checkSheetToDownloadInAWorkbook(sheetList).length) {

            const workbookName = currentExportingWorbook.name;

            if (backupAllWorkbookstoOneDrive) {
                dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, isWorkbookDownloading: true, exportWorkbook: null, sheetsExportedIntoWorkbook: [], workbooksExported: newWorkbooksExported })
                dispatch(uploadExcelToOneDrive(workbook, currentExportingWorbook.sheetListData.map((sheet) => sheet.id), currentExportingWorbook, "allWorkbooks", () => callback(currentExportingWorbook)))
            }
            else {
                dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, isWorkbookDownloading: false, exportWorkbook: null, sheetsExportedIntoWorkbook: [], currentDownladingWorkbook: {} })
                dispatch(uploadExcelToOneDrive(workbook, checkSheetToDownloadInAWorkbook(currentExportingWorbook.sheetList).map((sheet) => sheet.id), currentExportingWorbook, "", () => callback(currentExportingWorbook)))
            }
        } else {

            if (backupAllWorkbookstoOneDrive)
                dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, exportWorkbook: workbook, sheetsExportedIntoWorkbook: newSheetListIntoWorkbook, isWorkbookDownloading: true, workbooksExported: newWorkbooksExported })
            else
                dispatch({ type: ACTION_TYPES.DOWNLOAD_WORKBOOK, exportWorkbook: workbook, sheetsExportedIntoWorkbook: newSheetListIntoWorkbook, isWorkbookDownloading: true, currentDownladingWorkbook: currentExportingWorbook })
        }

    } catch (err) {
        throw new Error(err);
    }


}

export const checkSheetToDownloadInAWorkbook = (sheetList) => {
    try {
        const filteredSheetListData = sheetList.filter(item => {
            if (item.sheetType === "checkinagent") {
                const parentSheet = sheetList.find((sheet) => sheet.id === item.referenceSheetId);
                const assigneeCol = parentSheet.columns.find((col) => col.type === "Assignee")
                return assigneeCol.listData && JSON.parse(assigneeCol.listData).length !== 0;

            } else {
                return item.sheetType !== "xPad";
            }
        })

        return filteredSheetListData;
    } catch (error) {
        console.error("Error in checkSheetToDownloadInAWorkbook", error);
    }
}
export const processParsedObject = (cellData) => {
    try {
        const parsedValue = JSON.parse(cellData);
        if (parsedValue && typeof parsedValue === 'object' && parsedValue.value) {
            const result = updateValue(parsedValue.value);
            return result;
        }
    } catch (error) {
        console.error("JSON parsing error:", error.message, cellData);
    }
    return cellData;
};
export const processPersonColumn = (cellData) => {
    try {
        if (cellData?.startsWith('[')) {
            const parsedData = JSON.parse(cellData);
            if (Array.isArray(parsedData) && parsedData.every(item => item.username)) {
                return parseAndSortValuesForPersonColHelperForExport(cellData).join('; ');
            }
        }
        return cellData;
    } catch (error) {
        console.error("Error parsing person column data:", error.message, cellData);
    }
    return cellData;
};

export const createExcelJsWorkbook = async (gridRef, fileName, existingworkbook, sheetType, sheetId, isWbCreateForOneDrive, azureIntegrationInfo) => {
    try {
        let workbook = existingworkbook || new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(`${cropStringIfNameExceeds31Chars(fileName)}`);
        const allheaderNames = getAllColumnHeaders(gridRef);
        const allFields = getAllColumnfields(gridRef);
        const allColWidths = getAllColumnWidths(gridRef);
        const allColTypes = getAllColumnTypes(gridRef);
        const predeccessorCol = findPredeccessorCol(gridRef)[0]
        const { colGroupingExists, groups } = checkIfColGroupingExist(gridRef)
        if (colGroupingExists) {
            const positionsToMerge = findMatchingPositions(allheaderNames, groups);
            const groupRow = worksheet.addRow(Array.from({ length: allheaderNames.length }, () => ""));

            positionsToMerge.forEach((pos) => {
                const startCol = pos.startIndex + 1;
                const endCol = startCol + pos.length - 1;
                worksheet.mergeCells(groupRow.number, startCol, groupRow.number, endCol);  //worksheet.mergeCells(startRow,startColumn,endRow,endColumn)
                const mergedCell = worksheet.getCell(groupRow.number, startCol);
                mergedCell.value = pos.name;
                mergedCell.font = { bold: true, size: 12 };
                mergedCell.alignment = { horizontal: 'center', vertical: 'middle' };
            });
        }
        const headerRow = worksheet.addRow(allheaderNames)
        headerRow.eachCell((cell) => {
            cell.font = { bold: true };
        });
        worksheet.columns = [
            { width: allColWidths[0] / 7 },
            ...allColWidths.slice(1).map(width => ({ width: width / 7 }))
        ];
        const rowData = [];
        let numberOfCheckInRows = 0;

        gridRef.current.api.forEachNode((node, index) => {
            rowData.push(node.data);
        });

        const parentLIds = new Set();
        rowData.forEach(row => {
            const parts = row.l_id?.split('.');
            if (parts) {
                for (let i = 1; i < parts.length; i++) {
                    parentLIds.add(parts.slice(0, i).join('.'));
                }
            }
        });

        rowData.forEach((row, index) => {
            const { id, l_id } = row;
            let data = []
            data[0] = row.primaryCol_es?.value
                ? (row.isAzureRow ? "🔷 " : "") + row.primaryCol_es.value
                : '';
            allFields.forEach(field => {
                let cellValue = row[field] && row[field].value ? row[field].value : '';
                if (cellValue && cellValue?.includes("⊗⇔≈")) {
                    cellValue = cellValue?.replace(/⊗⇔≈/g, ", ");
                }
                let cellData = field === predeccessorCol ? getIndexesFromNodeArray(cellValue, gridRef.current, numberOfCheckInRows).indexString : cellValue
                if (isStringifiedArrayWithPropName(cellData)) {
                    cellData = parseAndSortValues(cellData);
                }
                else if (cellData && containsRowId(cellData)) {
                    let parsedCellData = JSON.parse(cellData)
                    cellData = parsedCellData.rowId === 'None' ? `${parsedCellData.value} (Default)` : parsedCellData.value;
                } else if (typeof cellData === 'string' && cellData.startsWith('{')) {
                    cellData = processParsedObject(cellData)
                }
                else {
                    cellData = processPersonColumn(cellData);
                }
                data.push(cellData);
            });


            const newRow = worksheet.addRow(data);

            const primaryColData = row.primaryCol_es;
            const indentLevel = calculateIndentationLevel(id, l_id)
            const isParentRow = parentLIds.has(l_id);
            if ((Object.keys(primaryColData?.cellStyle || []).length > 0 || primaryColData?.format?.length > 0 || indentLevel > 0 || isParentRow)) {
                const primaryColCell = newRow.getCell(1);
                applyStyles(primaryColCell, primaryColData, indentLevel);
                if (isParentRow) {
                    primaryColCell.font = { ...primaryColCell.font, bold: true };
                }
            }

            allFields.forEach((field, fieldIndex) => {
                const colType = allColTypes[`${field}`];
                const isMdv = colType === 'multi dimensional value';
                const cellData = row[field];
                if (cellData && (Object?.keys(cellData?.cellStyle ?? {}).length > 0 || cellData?.format?.length > 0 || isMdv)) {
                    const cell = newRow.getCell(fieldIndex + 2);
                    applyStyles(cell, cellData, 0, isMdv);
                }
                const cell = newRow.getCell(fieldIndex + 2);
                cell.alignment = { wrapText: true }
            });

        })

        if (isWbCreateForOneDrive) {
            const newRow = worksheet.addRow();
            newRow.getCell(1).value = sheetId;
            newRow.hidden = true;
        }

        if (sheetType === "checkinagent") {
            worksheet.addRow([]);
        }

        const colTypes = getLastRowContent(gridRef, colGroupingExists, sheetType, azureIntegrationInfo)
        if (sheetType === 'checkinagent') worksheet.addRow([]);
        worksheet.addRow(colTypes)

        return workbook;
    } catch (err) {

        console.log('error in createExcelJsWorkbook', err);

    }

}

export function cropStringIfNameExceeds31Chars(input) {
    if (input.length > 31) {
        return input.slice(0, 31);
    }
    return input;
}
